<template>
    <div class="sensor-detail" :class="[$mq]">
        <div class="topbar-container">
            <div class="return-icon" @click="close()"></div>
            <div class="sensor-top-container">
                <div class="datepicker-container">
                    <div class="filter date">
                        <div class="title-date" v-if="![$mq].includes('portrait')">{{ $t('assets.date.filter_by') }}</div>

                        <div class="filter-pill-wrapper">
                            <button type="button" class="filter-trigger" @click="toggleOptionsDropdown" :aria-label="$t('assets.date.filter_by')">
                                <span class="filter-trigger-text">{{ filterDisplayText }}</span>
                                <span class="filter-trigger-caret" :class="{ open: showOptionsDropdown }"></span>
                            </button>

                            <template v-if="showOptionsDropdown">
                                <div class="options-backdrop" @click="closeOptionsDropdown"></div>
                                <div class="options-dropdown">
                                    <button
                                        v-for="option in optionsDate"
                                        :key="option.value"
                                        type="button"
                                        class="options-dropdown-item"
                                        :class="{
                                            active: currentFilters.type_date === option.value,
                                            'has-meta': option.value === 'custom' && hasRangeSelected
                                        }"
                                        @click="selectFilterOption(option)"
                                    >
                                        <span class="options-dropdown-item-label">{{ option.name }}</span>
                                        <span v-if="option.value === 'custom' && hasRangeSelected" class="options-dropdown-item-meta">
                                            {{ rangeDisplayText }}
                                        </span>
                                    </button>
                                </div>
                            </template>

                            <div v-if="hasOpenedRangePicker" v-show="showRangePicker" class="range-backdrop" @click.self="closeRangePicker">
                                <div class="range-popover andy-range-calendar" @click.stop>
                                    <functional-calendar
                                        ref="rangeCalendar"
                                        v-model="calendarRange"
                                        :is-date-range="true"
                                        :date-format="'yyyy-mm-dd'"
                                        :day-names="$t('calendar.day_names')"
                                        :month-names="$t('calendar.months')"
                                        :short-month-names="$t('calendar.months')"
                                        :change-month-function="true"
                                        :change-year-function="true"
                                        :limits="pickerLimits"
                                        :new-current-date="initialPickerDate"
                                        @input="onRangeChosen"
                                        @opened="onCalendarOpened"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="right-side">
                    <div class="refresh-icon" :class="$mq" @click="filterDate()">
                        <img :src="cssImages + 'reload-retry-gray.svg'" alt="" />
                        <span v-if="![$mq].includes('portrait')"> {{ $t('sensor.refresh') }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showSensorBanner">
            <Message
                :key="sensorBannerKey"
                :message="sensorBannerMessage"
                :hasActions="false"
                :type="sensorBannerType"
                :customClass="['sensor-alert-banner', sensorBannerCustomClass]"
            />
        </div>
        <div class="detail-container">
            <sensor-chart 
                :sensor="sensor"
                :sensorChartValues="sensorChartValues"
                :sensorType="sensorType"
                :sensorStatus="sensorStatus"
                :dateFilter="currentFilters"
                :hoveredAlertTimestamp="hoveredAlertTimestamp"
                :selectedAlertTimestamp="selectedAlertTimestamp"
                :sensorHistory="chartSensorHistory"
                @sensor-alert-changed="onSensorAlertChanged"
            />
            <div class="sensor-history" v-if="!loadingSensorHistory && sensorHistory && sensorHistory.length > 0">
                <div class="sensor-history-button" @click="showSensorHistory = !showSensorHistory">
                    <div class="i-andy-history"></div>
                    {{ $t('sensor.history.button') }}
                </div>
                <div class="sensor-history-container" v-if="showSensorHistory">
                    <div class="sensor-history-items">
                        <div class="sensor-history-item" v-for="(item, index) in sensorHistory" :key="item.id || index">
                            <div class="sensor-history-item-status">
                                <div v-if="item.reason == 'On'" class="sensor-history-item-on">
                                    <div class="circle"></div>
                                    {{ $t('sensor.history.on') }}
                                </div>
                                <div v-else class="sensor-history-item-off">
                                    <div class="circle"></div>
                                    {{ $t('sensor.history.off') }}
                                </div>
                            </div>
                            <div class="sensor-history-item-time">
                                {{ formatDate(item.time, 'DD/MM/YYYY HH:mm') }},
                            </div>
                            <div>
                                {{ getUserName(item) + (item.reason !== 'On' ? ',' : '') }}
                            </div>
                            <div v-if="item.reason != 'On'">
                                {{ parseReason(item.reason) }},
                            </div>
                            <div v-if="item.reason != 'On'">
                                {{ item.description ? item.description : $t('sensor.history.no_description') }}
                            </div>
                        </div>
                    </div>
                    <div class="i-andy-close i-andy-s sensor-history-close" @click="showSensorHistory = false"></div>
                </div>
            </div>
            <div class="issues-selector">
                <Tabs
                    :tabOneText="$t('sensor.alerts', { number: sensor.alerts })"
                    :tabTwoText="totalPeriodIssues ? ($t('sensor.closed_issues', { number: totalPeriodIssues })) : $t('sensor.closed_issues', { number: 0 })"
                    :tabThreeText="openIssuesTabLabel"
                    :selected="selectedTab"
                    @switchClick="switchIssuesTab"
                ></Tabs>
            </div>
            <div class="list-issues-card-container hide-scrollbar" v-if="selectedTab === 'tabOne'">
                <div class="list-issues-content hide-scrollbar">
                    <!-- <template v-if="sensorAlerts"> -->
                    <template v-if="sensorAlerts && sensorAlerts.length > 0">
                        <sensor-alert-card
                            v-for="(alertItem, index) in totalAlertsPaginated"
                            :key="index"
                            :sensor="alertItem"
                            :interactive="true"
                            cardClass="alert-card"
                            @alert-hover="onAlertHover"
                            @alert-leave="onAlertLeave"
                            @alert-select="onAlertSelect"
                        />
                        <div class="pagination" v-if="page > 1 || perPage * page < total">
                            <div class="item prev" v-if="page > 1" @click="page_prev()"></div>
                            <div class="item next" v-if="perPage * page < total" @click="page_next()"></div>
                        </div>
                    </template>
                    <template v-else>
                        <EmptyPage :title="$t('sensor.empty_alert')" :description="$t('sensor.empty_description', { date: appliedDateText })"></EmptyPage>
                    </template>
                </div>
            </div>
            <div class="list-issues-card-container hide-scrollbar" v-else-if="selectedTab === 'tabTwo'">
                <div class="list-issues-content hide-scrollbar">
                    <template v-if="periodIssuesPaginated.length > 0">
                        <sensor-alert-card :sensor="issueList" :cardClass="getIssueCardClass(issueList)" v-for="(issueList, index) in periodIssuesPaginated" :sensorName="sensor.name" :key="index" />
                        <div class="pagination" v-if="pagePeriodIssues > 1 || perPagePeriodIssues * pagePeriodIssues < totalPeriodIssues">
                            <div class="item prev" v-if="pagePeriodIssues > 1" @click="periodIssuesPagePrev()"></div>
                            <div class="item next" v-if="perPagePeriodIssues * pagePeriodIssues < totalPeriodIssues" @click="periodIssuesPageNext()"></div>
                        </div>
                    </template>

                    <template v-else>
                        <EmptyPage :title="$t('sensor.empty_issues')" :description="$t('sensor.empty_description', { date: appliedDateText })"></EmptyPage>
                    </template>
                </div>
            </div>
            <div class="list-issues-card-container hide-scrollbar" v-else-if="selectedTab === 'tabThree'">
                <div class="list-issues-content hide-scrollbar">
                    <template v-if="openIssuesList.length > 0">
                        <sensor-alert-card :sensor="issueList" cardClass="open-issue-card" v-for="(issueList, index) in openIssuesList" :sensorName="sensor.name" :key="issueList.checklist_id || issueList.issue_id || index" />
                        <div class="pagination" v-if="pageOpenIssues > 1 || perPageOpenIssues * pageOpenIssues < totalOpenIssues">
                            <div class="item prev" v-if="pageOpenIssues > 1" @click="openIssuesPagePrev()"></div>
                            <div class="item next" v-if="perPageOpenIssues * pageOpenIssues < totalOpenIssues" @click="openIssuesPageNext()"></div>
                        </div>
                    </template>

                    <template v-else>
                        <EmptyPage :title="$t('sensor.empty_open_issues')"></EmptyPage>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Tabs from '../ui/Tabs.vue'
import EmptyPage from '../../layout/EmptyPage.vue'
import SensorAlertCard from './sensorAlertCard.vue'
import SensorChart from './SensorChart.vue'
import i18n from '@/i18n'
import Message from '@/components/domain/ui/Message'
export default {
    components: { SensorAlertCard, EmptyPage, Tabs, SensorChart, Message },
    name: 'sensorDetail',
    props: {
        sensorID: {
            type: [String, Object],
            default: false
        },
        filtersActive: {
            type: Object,
            default: false
        }
    },
    data() {
        return {
            maxDate: moment().format('YYYY-MM-DD'),
            minDate: true,
            showButtonFilter: false,
            currentFilters: {
                type_date: 'today',
                date: {
                    start_date: false,
                    end_date: false
                }
            },
            emptyFilters: {
                type_date: 'today',
                date: {
                    start_date: false,
                    end_date: false
                }
            },
            numberOpenIssues: false,
            selectedTab: 'tabOne',
            page: 1,
            perPage: 21,
            total: false,
            totalAlertsPaginated: [],
            openIssuesList: [],
            totalOpenIssues: 0,
            openIssuesRequestId: 0,
            pageOpenIssues: 1,
            perPageOpenIssues: 21,
            periodIssuesPaginated: [],
            pagePeriodIssues: 1,
            perPagePeriodIssues: 21,
            totalPeriodIssues: 0,
            cssImages: css_image_url,
            sensorHistory: [],
            sensorHistoryBefore: null,
            showSensorHistory: true,
            loadingSensorHistory: false,
            reason: {
                101: { id: '101', name: 'answer.justify.cleaning' },
                102: { id: '102', name: 'answer.justify.broken' },
                103: { id: '103', name: 'answer.justify.unavailable' }
            },
            showRangePicker: false,
            showOptionsDropdown: false,
            hasOpenedRangePicker: false,
            calendarRange: {},
            initialPickerDate: new Date(),
            closeRangeTimeout: null,
            isHydratingRange: false,
            lastEmittedRange: { start: false, end: false },
            maxRangeMonths: 2,
            hoveredAlertTimestamp: null,
            selectedAlertTimestamp: null
        }
    },
    computed: {
        optionsDate() {
            const options = [
                { value: 'today', name: i18n.t('assets.date.today_last_hours') },
                { value: 'yesterday', name: i18n.t('assets.date.yesterday') },
                { value: 'week', name: i18n.t('assets.date.last_week') },
                { value: 'month', name: i18n.t('assets.date.last_month') }
            ]
            if (this.sensorType == 'testo') {
                return options.concat([{ value: 'custom', name: i18n.t('assets.date.custom') }])
            }

            return options.concat([
                { value: 'quarter', name: i18n.t('assets.date.quarter') },
                { value: 'custom', name: i18n.t('assets.date.custom') }
            ])
        },
        sensorType() {
            //To validate if the sensor is testo or dotbox
            const config = this.$store.getters['login/getConfig']
            return config && config.sensor && config.sensor.type ? config.sensor.type : false
        },
        actionPlan() {
            if (this.sensor && this.sensor.config && this.sensor.config.tpl_id) {
                let tpl = this.$store.getters['issues/getTemplates'](this.sensor.config.tpl_id)
                if (tpl) {
                    return tpl.name
                }
            }
            return false
        },
        issueStates() {
            return this.$store.getters['getIssueStates']
        },
        sensorChartValues() {
            if (this.sensor && this.sensor.id) {
                let sensor = this.$store.getters['sensor/getSensorValues'](this.sensor.id)
                if (sensor && sensor.alerts && sensor.alerts > 0) {
                    this.hasAlerts = sensor.alerts
                }
                if (sensor && sensor.values && Object.values(sensor.values).length > 0) {
                    return sensor.values
                } else {
                    return 'nodata'
                }
            }
            return false
        },
        chartSensorHistory() {
            const history = Array.isArray(this.sensorHistory) ? this.sensorHistory : []
            if (!this.sensorHistoryBefore) {
                return history
            }

            return [this.sensorHistoryBefore, ...history]
        },
        sensorData() {
            return this.$store.getters['sensor/getList'](this.sensorID)
        },
        sensor() {
            return this.$store.getters['sensor/getSensorValues'](this.sensorID)
        },
        sensorAlerts() {
            let sensor = this.$store.getters['sensor/getSensorValues'](this.sensorID)
            if (sensor && sensor.values && Object.keys(sensor.values).length > 0) {
                let aSensor = []
                for (let index in this.sensor.values) {
                    if (sensor.values[index].alert) {
                        let sensorResult = {
                            id: sensor.id,
                            name: sensor.name,
                            data: { ref: sensor.values[index] },
                            timestamp: parseInt(index)
                        }
                        aSensor.push(sensorResult)
                    }
                }
                // sort by timestamp
                aSensor.sort((a, b) => {
                    return b.timestamp - a.timestamp
                })

                this.total = aSensor.length
                this.totalAlertsPaginated = aSensor.slice(this.perPage * (this.page - 1), this.perPage * this.page)
                return aSensor
            }

            return false
        },
        issuesInPeriod() {
            const sensor = this.sensor
            if (!sensor || !sensor.values || Object.keys(sensor.values).length === 0) {
                return []
            }
            const issuesById = {}
            for (let index in sensor.values) {
                const value = sensor.values[index]
                if (value && value.issue) {
                    const item = this.buildIssueItem(sensor, value, index)
                    const key = item.issue_id || item.checklist_id || index
                    if (!issuesById[key] || issuesById[key].timestamp < item.timestamp) {
                        issuesById[key] = item
                    }
                }
            }
            const result = Object.values(issuesById)
            result.sort((a, b) => b.timestamp - a.timestamp)
            return result
        },
        openIssuesInPeriod() {
            return this.issuesInPeriod.filter((item) => this.isIssueOpen(item.status))
        },
        hasOpenIssuesInPeriod() {
            return this.openIssuesInPeriod.length > 0
        },
        sensorStatus() {
            if (!this.sensor) return 'nodata'
            if (this.sensorData.status === 1) return 'nodata'

            if (!this.sensor.config || this.sensor.config.alert === false) return 'offline'

            if (this.hasOpenIssuesInPeriod) return 'issue'

            if (!this.hasCurrentAlert) return 'working'

            return 'alert'
        },
        hasCurrentAlert() {
            let hasCurrentAlert = false
            if (this.sensorData.status !== 1 && this.sensor.last_value && this.sensor.config) {
                const current = this.sensor.last_value.value
                const cond = this.sensor.config.condition
				const rawVal = this.sensor.config.value
				if (cond === '<') hasCurrentAlert = !isNaN(Number(rawVal)) ? current < Number(rawVal) : false
				else if (cond === '>') hasCurrentAlert = !isNaN(Number(rawVal)) ? current > Number(rawVal) : false
				else if (cond === '<>' || cond === '!<>') {
					if (typeof rawVal === 'string' && rawVal.includes(',')) {
						const parts = rawVal.split(',').map(v => parseFloat(String(v).trim()))
						const min = parts[0]
						const max = parts[1]
						if (!isNaN(min) && !isNaN(max)) {
							const inside = current >= min && current <= max
							hasCurrentAlert = cond === '<>' ? inside : !inside
						}
					}
				}
            }
            return hasCurrentAlert
        },
        appliedDateText() {
            const applied = this.emptyFilters && this.emptyFilters.type_date ? this.emptyFilters : this.currentFilters
            const type = applied && applied.type_date ? applied.type_date : 'today'
            const opt = this.optionsDate.find(o => o.value === type)
            const label = opt && opt.name ? String(opt.name) : String(type)
            return label && label.length > 0 ? label.charAt(0).toLowerCase() + label.slice(1) : label
        },
        /**
         * Indica si el filtro personalizado tiene rango (inicio y fin) seleccionado.
         */
        hasRangeSelected() {
            const date = this.currentFilters && this.currentFilters.date ? this.currentFilters.date : null
            return !!(date && date.start_date && date.end_date)
        },
        /**
         * Texto resumido del rango seleccionado en formato compacto DD/MM/YY.
         */
        rangeDisplayText() {
            const date = this.currentFilters && this.currentFilters.date ? this.currentFilters.date : null
            const start = this.formatShortDate(date ? date.start_date : null)
            const end = this.formatShortDate(date ? date.end_date : null)
            if (!start && !end) return this.$t('assets.date.range')
            if (start && !end) return start
            if (!start && end) return end
            return `${start} - ${end}`
        },
        /**
         * Texto mostrado en el botón disparador del filtro: opción activa o, si es
         * "custom" con rango completo, el resumen del rango.
         */
        filterDisplayText() {
            const type = this.currentFilters && this.currentFilters.type_date ? this.currentFilters.type_date : 'today'
            if (type === 'custom' && this.hasRangeSelected) {
                return this.rangeDisplayText
            }
            const option = this.optionsDate.find(o => o.value === type)
            return option ? option.name : this.$t('assets.date.filter_by')
        },
        /**
         * Límites min/max para el calendario: nunca permite seleccionar fechas futuras
         * (max = hoy). Se aplica vía la prop `limits` de vue-functional-calendar.
         */
        pickerLimits() {
            return {
                min: '1900-01-01',
                max: moment().format('YYYY-MM-DD')
            }
        },
        openIssuesTabLabel() {
            const num = this.totalOpenIssues || 0
            const isMobile = [this.$mq].includes('portrait')
            const key = isMobile ? 'sensor.open_issues' : 'sensor.issues'
            const text = this.$t(key, { number: num })
            const iconClass = num > 0 ? 'i-andy-exclamation i-andy-xs' : ''
            return num > 0 ? `<span class="${iconClass}" style="padding:3px; color: #fff; background-color: #BE1E1E; border-radius: 50px; margin-right:8px"></span>${text}` : this.$t(key, { number: 0 })
        },
        showSensorBanner() {
            if (!this.sensor.config) return false
            if (this.hasOpenIssuesInPeriod && this.sensor.config.issue_message) return true
            return Boolean(this.sensor.config.alert_message && this.hasCurrentAlert && !this.hasOpenIssuesInPeriod)
        },
        sensorBannerKey() {
            return this.hasOpenIssuesInPeriod ? 'issue' : 'alert'
        },
        sensorBannerMessage() {
            if (this.hasOpenIssuesInPeriod && this.sensor.config.issue_message) {
                return this.$t('sensor.issue_message', { message: this.sensor.config.issue_message })
            }
            return this.$t('sensor.alert_message', { message: this.sensor.config.alert_message })
        },
        sensorBannerType() {
            return this.hasOpenIssuesInPeriod ? 'error' : 'warning'
        },
        sensorBannerCustomClass() {
            return this.hasOpenIssuesInPeriod ? 'sensor-detail-issue-message' : ''
        }
    },
    methods: {
        calcDate() {
            this.currentFilters.date && this.currentFilters.date.start_date ? (this.maxDate = moment(this.currentFilters.date.start_date).format('YYYY-MM-DD')) : true
            this.currentFilters.date && this.currentFilters.date.end_date ? (this.minDate = moment(this.currentFilters.date.end_date).format('YYYY-MM-DD')) : true
            // Force update object
            this.currentFilters = { ...this.currentFilters }
        },
        /**
         * Devuelve la fecha formateada como "DD/MM/YY" (longitud fija, predecible
         * y compacta para mostrar rangos sin truncar). Devuelve cadena vacía si el
         * valor no es válido.
         * @param {string} value Fecha en formato YYYY-MM-DD o YYYY/MM/DD
         * @returns {string}
         */
        formatShortDate(value) {
            if (!value) return ''
            const parsed = moment(value, ['YYYY-MM-DD', 'YYYY/MM/DD'], true)
            if (!parsed.isValid()) return ''
            return parsed.format('DD/MM/YY')
        },
        /**
         * Abre o cierra el popover del calendario de rango. Al abrir, posiciona el
         * calendario en el mes adecuado e hidrata la selección previa.
         */
        toggleRangePicker() {
            if (this.showRangePicker) {
                this.closeRangePicker()
                return
            }
            this.initialPickerDate = this.computeInitialPickerDate()
            this.hasOpenedRangePicker = true
            this.showRangePicker = true
            this.$nextTick(() => this.applyHydratedRange(0))
        },
        /**
         * Alterna la visibilidad del dropdown de opciones de filtro de fecha.
         */
        toggleOptionsDropdown() {
            this.showOptionsDropdown = !this.showOptionsDropdown
        },
        /**
         * Cierra el dropdown de opciones de filtro.
         */
        closeOptionsDropdown() {
            this.showOptionsDropdown = false
        },
        /**
         * Aplica una opción del dropdown de filtro: si es "custom" abre el calendario
         * de rango; si se cambia desde "custom" a otra, limpia el rango previo.
         * @param {Object} option Opción seleccionada (con `value` y `name`)
         */
        selectFilterOption(option) {
            if (!option) return
            this.closeOptionsDropdown()
            const previousType = this.currentFilters && this.currentFilters.type_date ? this.currentFilters.type_date : null
            this.currentFilters.type_date = option.value
            if (option.value === 'custom') {
                this.$nextTick(() => {
                    if (!this.showRangePicker) {
                        this.toggleRangePicker()
                    }
                })
                return
            }
            if (previousType === 'custom') {
                this.currentFilters.date.start_date = false
                this.currentFilters.date.end_date = false
            }
        },
        /**
         * Cierra el popover del calendario y cancela cualquier timeout de auto-cierre
         * pendiente.
         */
        closeRangePicker() {
            this.showRangePicker = false
            if (this.closeRangeTimeout) {
                clearTimeout(this.closeRangeTimeout)
                this.closeRangeTimeout = null
            }
        },
        /**
         * Calcula la fecha en la que debe abrirse el calendario: la fecha de inicio
         * del rango actual si existe, o el día de hoy en su defecto.
         * @returns {Date}
         */
        computeInitialPickerDate() {
            const start = this.currentFilters && this.currentFilters.date ? this.currentFilters.date.start_date : null
            if (start) {
                const parsed = moment(start)
                if (parsed.isValid()) return parsed.toDate()
            }
            return new Date()
        },
        /**
         * Callback del evento `@opened` del calendario. Aplica el monkey-patch para
         * el clic del mismo día y re-hidrata el rango ya seleccionado.
         */
        onCalendarOpened() {
            this.$nextTick(() => {
                this.patchSameDayClick()
                this.applyHydratedRange(0)
            })
        },
        /**
         * Monkey-patch al método `clickDay` interno de vue-functional-calendar para:
         *  - Permitir seleccionar un único día como rango (mismo día como inicio y fin).
         *  - Recortar automáticamente el rango cuando se intenta superar el máximo
         *    permitido (`maxRangeMonths`), en lugar de bloquear la selección.
         */
        patchSameDayClick() {
            const cal = this.$refs.rangeCalendar
            if (!cal || cal._sameDayPatched) return
            if (typeof cal.clickDay !== 'function') return

            const originalClickDay = cal.clickDay.bind(cal)
            const self = this
            cal._sameDayPatched = true
            cal.clickDay = function (item) {
                const range = cal.calendar && cal.calendar.dateRange ? cal.calendar.dateRange : null
                const hasStart = range && range.start && range.start.date
                const hasEnd = range && range.end && range.end.date

                if (hasStart && !hasEnd && item) {
                    if (range.start.date === item.date) {
                        cal.calendar.dateRange.end.date = item.date
                        if (typeof cal.markChooseDays === 'function') cal.markChooseDays()
                        cal.$emit('input', cal.calendar)
                        return
                    }
                    if (self.exceedsMaxRange(range.start.date, item.date)) {
                        const clamped = self.clampRangeToMax(range.start.date, item.date)
                        if (clamped) {
                            cal.calendar.dateRange.start.date = clamped.start
                            cal.calendar.dateRange.end.date = clamped.end
                            if (typeof cal.markChooseDays === 'function') cal.markChooseDays()
                            cal.$emit('input', cal.calendar)
                            return
                        }
                    }
                }
                return originalClickDay(item)
            }
        },
        /**
         * Indica si la diferencia entre dos fechas supera el máximo permitido
         * (`maxRangeMonths`). Acepta formato YYYY-M-D o YYYY-MM-DD.
         * @param {string} startDate
         * @param {string} endDate
         * @returns {boolean}
         */
        exceedsMaxRange(startDate, endDate) {
            if (!startDate || !endDate) return false
            const start = moment(startDate, ['YYYY-M-D', 'YYYY-MM-DD'])
            const end = moment(endDate, ['YYYY-M-D', 'YYYY-MM-DD'])
            if (!start.isValid() || !end.isValid()) return false
            const diffMonths = Math.abs(end.diff(start, 'months', true))
            return diffMonths > this.maxRangeMonths
        },
        /**
         * Calcula el rango "máximo permitido" cuando el usuario intenta seleccionar
         * más allá del límite: toma como inicio la fecha más temprana entre `start` y
         * el clic, y como fin (`start + maxRangeMonths`) recortado a hoy si excede.
         * @param {string} startDate
         * @param {string} clickedDate
         * @returns {{start: string, end: string} | null}
         */
        clampRangeToMax(startDate, clickedDate) {
            const startMoment = moment(startDate, ['YYYY-M-D', 'YYYY-MM-DD'])
            const clickMoment = moment(clickedDate, ['YYYY-M-D', 'YYYY-MM-DD'])
            if (!startMoment.isValid() || !clickMoment.isValid()) return null

            const earliest = clickMoment.isBefore(startMoment) ? clickMoment : startMoment
            const today = moment().startOf('day')
            let latest = earliest.clone().add(this.maxRangeMonths, 'months')
            if (latest.isAfter(today)) latest = today

            return {
                start: earliest.format('YYYY-M-D'),
                end: latest.format('YYYY-M-D')
            }
        },
        /**
         * Hidrata el estado interno del calendario con el rango actualmente filtrado,
         * para que al re-abrir el popover se pinten los días previamente elegidos.
         * Se ejecuta con reintentos porque el componente hijo puede no estar listo
         * en el primer tick. Marca `isHydratingRange` para que `onRangeChosen`
         * ignore las emisiones de `@input` provocadas por esta carga programática.
         * @param {number} [attempt=0] Intento actual (reintenta hasta maxAttempts)
         */
        applyHydratedRange(attempt) {
            const maxAttempts = 10
            const currentAttempt = typeof attempt === 'number' ? attempt : 0
            const cal = this.$refs.rangeCalendar
            if (!cal || !cal.calendar || !cal.calendar.dateRange) {
                if (currentAttempt < maxAttempts) {
                    setTimeout(() => this.applyHydratedRange(currentAttempt + 1), 50)
                }
                return
            }

            const startRaw = this.currentFilters && this.currentFilters.date ? this.currentFilters.date.start_date : null
            const endRaw = this.currentFilters && this.currentFilters.date ? this.currentFilters.date.end_date : null
            // vue-functional-calendar uses dates WITHOUT leading zeros internally (e.g. "2026-5-12", not "2026-05-12")
            const startDate = startRaw ? moment(startRaw).format('YYYY-M-D') : false
            const endDate = endRaw ? moment(endRaw).format('YYYY-M-D') : false

            this.isHydratingRange = true

            cal.calendar.dateRange.start.date = startDate
            cal.calendar.dateRange.end.date = endDate

            if (startDate) {
                cal.calendar.currentDate = moment(startRaw).toDate()
            }

            if (typeof cal.initCalendar === 'function') {
                cal.initCalendar()
            } else if (typeof cal.markChooseDays === 'function') {
                cal.markChooseDays()
            }

            this.$nextTick(() => {
                this.isHydratingRange = false
            })
        },
        /**
         * Callback del evento `@input` del calendario. Propaga el rango elegido al
         * filtro (`currentFilters.date`) sólo cuando cambia de verdad, evitando
         * disparos por navegación de meses o por hidratación programática. Si el
         * rango se acaba de completar (transición de "sin end" a "con end"), cierra
         * el popover automáticamente con un pequeño delay.
         * @param {Object} calendar Estado completo emitido por la librería
         */
        onRangeChosen(calendar) {
            const range = calendar && calendar.dateRange ? calendar.dateRange : null
            if (!range) return

            const startDate = range.start && range.start.date ? range.start.date : false
            const endDate = range.end && range.end.date ? range.end.date : false

            const previousEnd = this.lastEmittedRange.end
            const previousStart = this.lastEmittedRange.start
            this.lastEmittedRange = { start: startDate, end: endDate }

            if (this.isHydratingRange) return

            const rangeChanged = previousStart !== startDate || previousEnd !== endDate
            if (!rangeChanged) return

            this.currentFilters.date.start_date = startDate ? moment(startDate).format('YYYY-MM-DD') : false
            this.currentFilters.date.end_date = endDate ? moment(endDate).format('YYYY-MM-DD') : false
            this.calcDate()

            const justCompletedRange = startDate && endDate && !previousEnd
            if (justCompletedRange) {
                if (this.closeRangeTimeout) clearTimeout(this.closeRangeTimeout)
                this.closeRangeTimeout = setTimeout(() => {
                    this.showRangePicker = false
                    this.closeRangeTimeout = null
                }, 250)
            }
        },
        close() {
            this.$emit('close', this.currentFilters)
        },
        formatDate(date, format) {
            if (typeof date === 'undefined' || date === null) {
                return ''
            }
            let formatedDate = moment.utc(date)
            formatedDate = formatedDate.local()
            const serverTimeDifference = this.$store.getters['getServerTimeDifference'] || 0
            if (serverTimeDifference !== 0) {
                formatedDate = formatedDate.add(serverTimeDifference, 'minutes')
            }

            return formatedDate.format(format)
        },
        filterDate() {
            const self = this
            self.$overlay.loading()
            this.hoveredAlertTimestamp = null
            this.selectedAlertTimestamp = null
            this.emptyFilters = JSON.parse(JSON.stringify(this.currentFilters))
            this.pagePeriodIssues = 1
            this.$emit('filterDate', { id: this.$route.params.id, filters: this.currentFilters })
            this.loadSensorHistory()
            this.loadOpenIssues(1)
        },
        switchIssuesTab(tab) {
            this.selectedTab = tab

            if (tab !== 'tabOne') {
                this.hoveredAlertTimestamp = null
                this.selectedAlertTimestamp = null
            }
        },
        resolveAlertMarkerTimestamp(alertItem) {
            if (!alertItem) {
                return null
            }

            const timestamp = Number(alertItem.timestamp)

            if (!isNaN(timestamp) && timestamp > 0) {
                return timestamp
            }

            const refDate = alertItem.data && alertItem.data.ref ? alertItem.data.ref.date : null

            if (typeof refDate === 'number' && !isNaN(refDate)) {
                return refDate > 1e12 ? Math.floor(refDate / 1000) : refDate
            }

            if (typeof refDate === 'string' && /^\d+$/.test(refDate)) {
                const numeric = Number(refDate)
                return numeric > 1e12 ? Math.floor(numeric / 1000) : numeric
            }

            const parsed = moment(refDate, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)

            if (parsed.isValid()) {
                return parsed.unix()
            }

            return null
        },
        onAlertHover(alertItem) {
            this.hoveredAlertTimestamp = this.resolveAlertMarkerTimestamp(alertItem)
        },
        onAlertLeave() {
            this.hoveredAlertTimestamp = null
        },
        onAlertSelect(alertItem) {
            this.selectedAlertTimestamp = this.resolveAlertMarkerTimestamp(alertItem)
        },
        getSensorId() {
            return (this.sensor && this.sensor.id) || this.sensorID || (this.$route.params && this.$route.params.id)
        },
        parseOpenIssuesResponse(response) {
            if (!response) {
                return { items: [], total: 0 }
            }

            const total = Number(response.total) || 0
            const payload = response.results

            if (payload == null || typeof payload === 'number') {
                return { items: [], total: typeof payload === 'number' ? payload : total }
            }

            let items = []
            if (Array.isArray(payload)) {
                items = payload
            } else if (typeof payload === 'object') {
                items = Object.values(payload).filter((entry) => entry && typeof entry === 'object')
            }

            return { items, total: total || items.length }
        },
        buildIssueItem(sensor, value, timestamp) {
            const resolvedTimestamp = this.resolveIssueTimestamp(value, timestamp)
            return {
                id: sensor.id,
                name: sensor.name,
                data: { ref: value },
                timestamp: resolvedTimestamp,
                issue_id: value.issue ? value.issue.issue_id : false,
                checklist_id: value.issue ? value.issue.id : false,
                status: value.issue ? value.issue.status : false
            }
        },
        resolveIssueTimestamp(value, fallback) {
            if (value && value.created_date) {
                const parsed = moment(value.created_date, 'YYYY-MM-DD HH:mm:ss', true)
                if (parsed.isValid()) return parsed.unix()
            }
            if (value && value.date != null) {
                if (typeof value.date === 'number') {
                    return value.date > 1e12 ? Math.floor(value.date / 1000) : value.date
                }
                const parsed = moment(value.date, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)
                if (parsed.isValid()) return parsed.unix()
            }
            const numericFallback = parseInt(fallback, 10)
            return isNaN(numericFallback) ? 0 : numericFallback
        },
        normalizeOpenIssueEntry(entry, sensor) {
            const ref = entry.data && entry.data.ref ? entry.data.ref : {
                value: entry.value != null ? entry.value : entry.sensor_value,
                date: entry.date
            }
            const timestamp = this.resolveIssueTimestamp({ created_date: entry.created_date, date: ref.date }, 0)

            return {
                id: sensor.id,
                name: sensor.name,
                data: {
                    ref: {
                        ...ref,
                        issue: {
                            id: entry.id,
                            issue_id: entry.issue_id,
                            status: entry.status
                        }
                    }
                },
                timestamp,
                issue_id: entry.issue_id,
                checklist_id: entry.id,
                status: entry.status
            }
        },
        isIssueOpen(status) {
            const states = this.issueStates
            if (!states || !status || !states[status] || typeof states[status].type === 'undefined') {
                return true
            }
            return states[status].type != 3
        },
        getIssueCardClass(item) {
            return this.isIssueOpen(item.status) ? 'open-issue-card' : 'closed-issue-card'
        },
        mapOpenIssueEntries(items, sensorId) {
            if (!sensorId || !items || !items.length) {
                return []
            }

            const sensorFromStore = this.$store.getters['sensor/getSensorValues'](sensorId) || this.$store.getters['sensor/getList'](sensorId)
            const sensor = sensorFromStore || { id: sensorId, name: '' }
            const seen = {}
            const result = []

            items.forEach((entry) => {
                if (!entry) {
                    return
                }

                const item = this.normalizeOpenIssueEntry(entry, sensor)
                const key = item.issue_id || item.checklist_id
                if (key && seen[key]) {
                    return
                }
                if (key) {
                    seen[key] = true
                }
                result.push(item)
            })

            return result
        },
        syncPeriodIssuesPagination(resetPage = false) {
            this.totalPeriodIssues = this.issuesInPeriod.length
            const maxPage = Math.max(1, Math.ceil(this.totalPeriodIssues / this.perPagePeriodIssues) || 1)

            if (resetPage) {
                this.pagePeriodIssues = 1
            } else if (this.pagePeriodIssues > maxPage) {
                this.pagePeriodIssues = maxPage
            }

            this.periodIssuesPaginated = this.issuesInPeriod.slice(
                (this.pagePeriodIssues - 1) * this.perPagePeriodIssues,
                this.pagePeriodIssues * this.perPagePeriodIssues
            )
        },
        loadOpenIssues(page, showOverlay = false) {
            const sensorId = this.getSensorId()
            if (!sensorId) {
                return Promise.resolve()
            }

            const targetPage = page || this.pageOpenIssues || 1
            const requestId = ++this.openIssuesRequestId
            this.pageOpenIssues = targetPage

            if (showOverlay) {
                this.$overlay.loading()
            }

            const params = {
                sensor_id: sensorId,
                offset: targetPage - 1,
                limit: this.perPageOpenIssues
            }

            return this.$store.dispatch('sensor/loadSensorOpenIssuesBySensor', params)
                .then((response) => {
                    if (requestId !== this.openIssuesRequestId) {
                        return
                    }
                    if (!response) {
                        this.openIssuesList = []
                        this.totalOpenIssues = 0
                        return
                    }
                    const parsed = this.parseOpenIssuesResponse(response)
                    this.totalOpenIssues = parsed.total
                    this.openIssuesList = this.mapOpenIssueEntries(parsed.items, sensorId)
                })
                .finally(() => {
                    if (showOverlay) {
                        this.$overlay.hide()
                    }
                })
        },
        page_next() {
            this.page++
            this.hoveredAlertTimestamp = null
            this.totalAlertsPaginated = this.sensorAlerts.slice(this.perPage * (this.page - 1), this.perPage * this.page)
        },
        page_prev() {
            this.page--
            this.hoveredAlertTimestamp = null
            this.totalAlertsPaginated = this.sensorAlerts.slice(this.perPage * (this.page - 1), this.perPage * this.page)
        },
        openIssuesPageNext() {
            if (this.perPageOpenIssues * this.pageOpenIssues >= this.totalOpenIssues) return
            this.loadOpenIssues(this.pageOpenIssues + 1, true)
        },
        openIssuesPagePrev() {
            if (this.pageOpenIssues <= 1) return
            this.loadOpenIssues(this.pageOpenIssues - 1, true)
        },
        periodIssuesPageNext() {
            if (this.perPagePeriodIssues * this.pagePeriodIssues >= this.totalPeriodIssues) {
                return
            }
            this.pagePeriodIssues++
            this.periodIssuesPaginated = this.issuesInPeriod.slice(
                (this.pagePeriodIssues - 1) * this.perPagePeriodIssues,
                this.pagePeriodIssues * this.perPagePeriodIssues
            )
        },
        periodIssuesPagePrev() {
            if (this.pagePeriodIssues <= 1) {
                return
            }
            this.pagePeriodIssues--
            this.periodIssuesPaginated = this.issuesInPeriod.slice(
                (this.pagePeriodIssues - 1) * this.perPagePeriodIssues,
                this.pagePeriodIssues * this.perPagePeriodIssues
            )
        },
        loadSensorHistory() {
            if (this.loadingSensorHistory) {
                return
            }
            
            const params = { sensorId: this.sensor.id }
            
            if (this.currentFilters && this.currentFilters.type_date) {
                if (this.currentFilters.type_date === 'custom') {
                    if (this.currentFilters.date && this.currentFilters.date.start_date) {
                        params.start_date = this.currentFilters.date.start_date
                    }
                    if (this.currentFilters.date && this.currentFilters.date.end_date) {
                        params.end_date = this.currentFilters.date.end_date
                    }
                } else {
                    params.start_date = this.currentFilters.type_date
                }
            }
            
            this.loadingSensorHistory = true
            this.$store.dispatch('sensor/loadSensorHistory', params)
                .then((payload) => {
                    const history = payload && Array.isArray(payload.items) ? payload.items : []
                    this.sensorHistory = history
                    this.sensorHistoryBefore = payload && payload.previous ? payload.previous : null
                })
                .finally(() => {
                    this.loadingSensorHistory = false
                })
        },
        onSensorAlertChanged(status) {
            if (this.sensor && this.sensor.id) {
                this.loadSensorHistory()
                this.loadOpenIssues(1)
            }
        },
        getUserName(user) {
            if (user.employee.name == '' && user.employee.surname == '') return '-'
            return [user.employee.name, user.employee.surname].filter(Boolean).join(' ')
        },
        parseReason(reason) {
            const id = JSON.parse(reason).id;
            return this.$t(this.reason[id].name);
        }
    },

    mounted() {
        this.currentFilters = { ...this.filtersActive }
        this.emptyFilters = JSON.parse(JSON.stringify(this.filtersActive))
        this.switchIssuesTab('tabOne')
        this.loadSensorHistory()
        this.loadOpenIssues(1)
    },
    beforeDestroy() {
        if (this.closeRangeTimeout) clearTimeout(this.closeRangeTimeout)
    },
    watch: {
        issuesInPeriod: {
            handler(newValue) {
                if (newValue && Array.isArray(newValue)) {
                    this.syncPeriodIssuesPagination(false)
                } else {
                    this.pagePeriodIssues = 1
                    this.totalPeriodIssues = 0
                    this.periodIssuesPaginated = []
                }
            },
            immediate: true
        },
        currentFilters: {
            handler: function (newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.emptyFilters)) {
                    this.showButtonFilter = true
                } else {
                    this.showButtonFilter = false
                }
            },
            deep: true
        },
        emptyFilters: {
            handler: function (newValue, oldValue) {
                // compare with empty filters if is the same
                if (JSON.stringify(newValue) !== JSON.stringify(this.emptyFilters)) {
                    this.showButtonFilter = true
                } else {
                    this.showButtonFilter = false
                }
            },
            deep: true
        },
        sensorID(id, oldId) {
            if (!id || id === oldId) {
                return
            }
            this.pageOpenIssues = 1
            this.openIssuesList = []
            this.totalOpenIssues = 0
            this.loadOpenIssues(1)
        },
        sensor(newValue, oldValue) {
            if (newValue && (!oldValue || oldValue.id !== newValue.id)) {
                this.loadSensorHistory()
            }
        },
        'sensor.config.alert'(newValue, oldValue) {
            if (newValue !== oldValue && this.sensor && this.sensor.id) {
                this.loadSensorHistory()
            }
        },
        'currentFilters.type_date'(newValue) {
            if (newValue !== 'custom') {
                this.showRangePicker = false
            }
        }
    }
}
</script>

<style lang="scss">
.sensor-detail {
    width: 100%;
    height: 100%;
    padding: 12px;
    @include display-flex();
    @include flex-direction(column);
    min-height: 0;

    .topbar-container {
        @include display-flex();
        @include flex-direction(row);
        @include justify-content(flex-start);
        @include align-items(center);
        margin-bottom: 6px;
        flex-shrink: 0;
        .return-icon {
            cursor: pointer;
            @include border-radius(4px);
            width: 45px;
            height: 45px;
            min-width: 45px;
            min-height: 45px;
            background-color: #fff;
            @include background($image: img('left_neutro.svg'), $size: 40%, $position: center center);

            &:hover {
                @include bgHover(#fff);
            }
        }
        .sensor-top-container {
            @include display-flex();
            @include flex-direction(row);
            @include justify-content(flex-end);
            @include align-items(center);
            padding-left: 12px;
            width: calc(100% - 50px);
            .sensor-ball {
                @include border-radius(50%);
                width: 10px;
                height: 10px;
                min-width: 10px;
                min-height: 10px;
                background-color: $done;
            }
            .sensor-name {
                @include font-size(m);
                @include text-ellipsis();
                font-family: $text-bold;
                padding-left: 12px;
                color: $neutro-s80;
            }
            .datepicker-container {
                width: 50%;

                .filter.date {
                    position: relative;
                    @include display-flex();
                    @include align-items(center);
                    gap: 8px;
                    margin-bottom: 0;

                    .filter-pill-wrapper {
                        position: relative;
                        @include display-flex();
                        @include align-items(center);

                        .filter-trigger {
                            font-family: inherit;
                            background-color: #fff;
                            border: 1px solid $neutro-s20;
                            color: $neutro-s80;
                            cursor: pointer;
                            text-align: left;
                            @include border-radius(4px);
                            @include display-flex();
                            @include align-items(center);
                            gap: 10px;
                            padding: 0 12px;
                            height: 45px;
                            min-width: 180px;

                            .filter-trigger-text {
                                @include font-size(sm);
                                font-family: $text;
                                color: $neutro-s80;
                                flex: 1;
                                @include text-ellipsis();
                            }

                            .filter-trigger-caret {
                                width: 8px;
                                height: 8px;
                                border-right: 2px solid $neutro-s60;
                                border-bottom: 2px solid $neutro-s60;
                                transform: rotate(45deg) translate(-2px, -2px);
                                transition: transform 0.2s ease;
                                flex-shrink: 0;

                                &.open {
                                    transform: rotate(-135deg) translate(-2px, -2px);
                                }
                            }

                            &:hover {
                                @include bgHover(#fff);
                            }
                            &:focus {
                                outline: none;
                            }
                        }
                    }

                    .options-backdrop {
                        position: fixed;
                        inset: 0;
                        z-index: 9997;
                        background-color: transparent;
                    }

                    .options-dropdown {
                        position: absolute;
                        top: calc(100% + 4px);
                        left: 0;
                        min-width: 100%;
                        max-width: 320px;
                        z-index: 9998;
                        background-color: #fff;
                        border: 1px solid $neutro-s20;
                        @include border-radius(4px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                        padding: 4px;
                        @include display-flex();
                        @include flex-direction(column);

                        .options-dropdown-item {
                            font-family: inherit;
                            background-color: transparent;
                            border: none;
                            text-align: left;
                            cursor: pointer;
                            @include border-radius(4px);
                            @include display-flex();
                            @include align-items(center);
                            @include justify-content(space-between);
                            gap: 12px;
                            padding: 10px 12px;
                            transition: background-color 0.12s ease;

                            .options-dropdown-item-label {
                                @include font-size(sm);
                                font-family: $text;
                                color: $neutro-s80;
                            }

                            .options-dropdown-item-meta {
                                @include font-size(xs);
                                font-family: $text;
                                color: $neutro-s60;
                            }

                            &.has-meta {
                                @include flex-direction(column);
                                @include align-items(flex-start);
                                gap: 2px;
                            }

                            &:hover {
                                background-color: $neutro;
                            }
                            &.active {
                                background-color: $neutro;
                                .options-dropdown-item-label {
                                    font-family: $text-bold;
                                }
                            }
                        }
                    }

                    .range-backdrop {
                        position: fixed;
                        inset: 0;
                        z-index: 9998;
                        background-color: rgba(0, 0, 0, 0.2);
                        @include display-flex();
                        @include justify-content(center);
                        @include align-items(flex-start);
                        padding-top: 120px;
                    }

                    .range-popover {
                        z-index: 9999;
                        background-color: $color-white;
                        @include border-radius(4px);
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
                        padding: 12px;
                        max-width: 360px;
                        width: 100%;
                    }
                    .range-popover.andy-range-calendar {
                        .vfc-top-date,
                        .vfc-top-date.vfc-underline {
                            color: $main !important;
                        }
                        .vfc-arrow-left,
                        .vfc-arrow-right {
                            border-color: $main !important;

                            &.vfc-disabled {
                                border-color: $neutro-s20 !important;
                            }
                        }
                        .vfc-base-start,
                        .vfc-base-end {
                            background: $main-t90 !important;
                        }
                        .vfc-calendar .vfc-day {
                            .vfc-today,
                            .vfc-today:not(:hover) {
                                background-color: transparent !important;
                                color: $main !important;
                                border: 1px solid $main-t50 !important;
                            }
                            .vfc-span-day.vfc-hover:hover {
                                background-color: $main-t90 !important;
                                color: $color-black !important;
                            }
                            .vfc-disabled,
                            .vfc-cursor-not-allowed {
                                background-color: transparent !important;
                                color: $neutro-s40 !important;
                                opacity: 0.5;

                                &:hover {
                                    background: transparent !important;
                                    color: $neutro-s40 !important;
                                }
                            }
                            .vfc-marked {
                                background-color: transparent !important;
                                color: $color-black !important;

                                &:before {
                                    background-color: $main-t90 !important;
                                }
                                &:hover {
                                    background-color: transparent !important;
                                }
                            }
                            .vfc-marked.vfc-start-marked,
                            .vfc-marked.vfc-end-marked,
                            .vfc-marked.vfc-borderd,
                            .vfc-base-start + .vfc-span-day,
                            .vfc-base-end + .vfc-span-day {
                                background-color: $main !important;
                                color: $color-white !important;
                                font-family: $text-bold !important;

                                &:hover {
                                    background-color: $main !important;
                                }
                                &:before {
                                    background: transparent !important;
                                }
                                &:after {
                                    color: $color-white !important;
                                    font-family: $text-bold !important;
                                }
                            }
                        }
                    }
                }
            }
            .right-side {
                //width: 50%;
                .refresh-icon {
                    @include display-flex();
                    @include justify-content(center);
                    @include align-items(center);
                    width: fit-content;
                    gap: 8px;
                    height: 45px;
                    @include border-radius(4px);
                    background-color: #fff;
                    cursor: pointer;
                    padding: 0px 24px;

                    img {
                        width: 60%;
                        height: 60%;
                        padding-right: 4px;
                    }

                    span {
                        @include font-size(sm);
                        color: $color-black;
                        font-family: Avenir-Bold;
                    }

                    &.portrait {
                        @include display-flex();
                        @include justify-content(center);
                        @include align-items(center);
                        width: 45px;
                        padding: 0;
                        img {
                            position: relative;
                            width: 60%;
                            height: 60%;
                            left: 2px;
                        }
                    }
                }
            }
        }
    }

    .document.plugin.sensor-alert-banner {
        flex-shrink: 0;
        max-height: 40vh;
        overflow-y: auto;

        .message,
        .keep-format,
        .typed-element,
        .preview,
        .read-more,
        .typing {
            max-height: none;
            height: auto;
            min-height: 0;
            overflow: visible;
            white-space: break-spaces;
            text-overflow: unset;
        }

        .preview,
        .read-more {
            display: block !important;
        }

        .preview *,
        .read-more * {
            display: inline;
            height: auto;
            white-space: normal;
        }
    }

    .warning {
        .message, .keep-format, .typed-element, .preview {
            border-radius: 4px !important;
            color: $color-black !important;
            background-color: #EAB308 !important;
        }
    }
    .error {
        .message, .keep-format, .typed-element, .preview {
            border-radius: 4px !important;
            color: $color-black !important;
            background-color: $error-light !important;
        }
        .message, .keep-format {
            background-color: $error-light !important;
        }
        .message {
            background-color: $error-light !important;
        }
    }
    .document.plugin.landscape.sensor-detail-issue-message.error .keep-format,
    .document.plugin.portrait.sensor-detail-issue-message.error .keep-format{
        border-radius: 4px !important;
        color: $color-black !important;
        background-color: $error-light !important;
    }
    .detail-container {
        width: 100%;
        flex: 1;
        min-height: 0;
        overflow: auto;
        overflow-x: hidden;

        .alert-box {
            @include border-radius(5px);
            margin-top: 10px;
            background-color: $default-sec-color;
            padding: 6px;
            margin-bottom: 10px;

            .text-content {
                @include display-flex();
                @include flex-direction(row);
                @include justify-content(flex-start);
                @include align-items(center);
                flex-wrap: wrap;

                .bold-text {
                    @include font-size(s);
                    font-family: $text-bold;
                    color: $neutro-s80;
                    margin-right: 5px;
                }
                .sensor-text {
                    @include font-size(s);
                    font-family: $text;
                    color: $neutro-s80;
                    padding-right: 5px;
                }
            }
        }

        .sensor-history {
            @include display-flex();
            @include flex-direction(column);

            .sensor-history-button {
                @include display-flex();
                @include align-items(center);
                gap: 8px;
                line-height: 20px;
                color: $color-secondary-500;
                margin-top: 16px;
                margin-bottom: 16px;
            }
            .sensor-history-container {
                @include display-flex();
                @include flex-direction(row);
                @include justify-content(space-between);
                @include align-items(flex-start);
                gap: 16px;

                .sensor-history-items {
                    @include display-flex();
                    @include flex-direction(column);
                    flex: 1;

                    .sensor-history-item {
                        @include display-flex();
                        @include flex-direction(row);
                        @include align-items(center);
                        margin-bottom: 8px;
                        white-space: nowrap;
                        min-width: fit-content;

                        .sensor-history-item-status {
                            width: 10%;
                            min-width: 120px;
                            flex-shrink: 0;

                            .sensor-history-item-on, .sensor-history-item-off {
                                //@include display-flex();
                                display: -webkit-box;
                                @include align-items(center);
                                background-color: $color-white;
                                border: 1px solid $color-neutral-300;
                                border-radius: 4px;
                                gap: 8px;
                                white-space: nowrap;
                                .circle {
                                    @include border-radius(1000px);
                                    height: 10px;
                                    width: 10px;
                                }
                                padding: 6px 8px;
                            }
                            .sensor-history-item-off {
                                .circle {
                                    background-color: $color-error-500;
                                }
                            }
                            .sensor-history-item-on {
                                .circle {
                                    background-color: $color-success-500;
                                }
                            }
                        }

                        .sensor-history-item-time {
                            margin-left: 8px;
                            font-weight: 600;
                            white-space: nowrap;
                            flex-shrink: 0;
                            margin-right: 4px;
                        }

                        > div:not(.sensor-history-item-status):not(.sensor-history-item-time) {
                            white-space: nowrap;
                            flex-shrink: 0;
                            margin-right: 4px;
                        }
                    }
                }
                .sensor-history-close {
                    height: 32px;
                    width: 32px;
                    padding: 7px;
                    background-color: $color-white;
                    border-radius: 4px;
                    border: solid 1px $color-neutral-300;
                    cursor: pointer;
                    flex-shrink: 0;
                    align-self: flex-start;
                }
            }
        }

        .issues-sensor {
            width: 100%;
            height: 100%;
        }
        .issues-selector {
            @include display-flex();
            @include justify-content(space-between);
            @include align-items(center);
            @include border-radius(4px);
            width: 700px;
            height: 40px;
            padding: 3px;
            background-color: #fff;
            margin: 0 auto;
            margin-bottom: 24px;
            margin-top: 16px;
            .selector {
                height: 48px;
                .option {
                    padding: 0 8px;
                    &.selected {
                        span {
                            color: #D7DAE3 !important;
                        }
                    }
                }
                .text {
                    @include display-flex();
                    @include justify-content(center);
                    @include align-items(center);
                    border-radius: 4px;
                    height: 100%;
                }
            }
        }
        .list-issues-card-container {
            width: 100%;
            height: auto;
            margin-bottom: 16px;

            .list-issues-content {
                display: grid;
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: 12px;

                > .empty-container {
                    grid-column: 1 / -1;
                    justify-self: center;
                    align-self: center;
                    min-height: 240px;
                }
                > .pagination {
                    grid-column: 1 / -1;
                }
            }
        }
    }
    .pagination {
        width: 100%;
        display: block;
        height: 50px;

        .item {
            width: calc(50% - 10px);
            height: 45px;
            float: left;
            @include border-radius(4px);
            @extend .interaction;

            &.next {
                @include background($image: img('right_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                float: right;
            }
            &.prev {
                @include background($image: img('left_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
            }
        }
    }
    &.portrait,
    &.landscape {
        .sensor-history {
            .sensor-history-container {
                position: relative;
                overflow-x: auto;
                overflow-y: visible;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: thin;

                .sensor-history-items {
                    min-width: max-content;

                    .sensor-history-item {
                        min-width: max-content;
                        width: max-content;

                        .sensor-history-item-status {
                            min-width: 135px;
                        }
                    }
                }

                .sensor-history-close {
                    position: sticky;
                    right: 0;
                    top: 0;
                    z-index: 10;
                    background-color: $color-white;
                }
            }
        }
        .error {
            .message, .keep-format, .typed-element, .preview {
                border-radius: 4px !important;
                color: $color-black !important;
                background-color: $error-light !important;
            }
            .message, .keep-format {
                background-color: $error-light !important;
            }
            .message {
                background-color: $error-light !important;
            }
        }
    }
    &.portrait {
        .detail-container {
            padding-bottom: 80px;
        }
        .detail-container {
            .list-issues-card-container {
                .list-issues-content {
                    grid-template-columns: 1fr;
                }
            }
        }
        .pagination {
            width: 100%;
            padding: 0px;
            display: block;
            height: 45px;
            // margin-top: 75px;
            position: relative;
            // top: -75px;
            margin-bottom: 10px;

            .item {
                width: calc(50% - 5px);
                height: 45px;
                margin: 0 0px;
                float: left;
                @include border-radius(4px);
                @extend .interaction;

                &.next {
                    @include background($image: img('right_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                    float: right;
                    @extend .interaction;
                }
                &.prev {
                    @include background($image: img('left_dark.svg'), $position: center center, $size: 20px, $color: $default-sec-color);
                }
            }
        }
        .issues-selector {
            width: 100%;
        }

        .sensor-top-container {
            @include display-flex();
            @include justify-content(space-between);
            @include align-items(center);
            flex-wrap: wrap;
            padding-left: 16px !important;
            row-gap: 8px;
        }
        .datepicker-container {
            width: calc(100% - 60px) !important;
            height: auto !important;
            .filter.date {
                width: 100% !important;
                height: auto !important;

                .filter-pill-wrapper {
                    flex: 1;

                    .filter-trigger {
                    width: 100% !important;
                        height: 38px;
                        padding: 0 10px;
                        min-width: 0;
                        gap: 8px;

                        .filter-trigger-caret {
                            width: 7px;
                            height: 7px;
                        }
                    }
                }

                .options-dropdown {
                    width: 100%;
                    max-width: none;
                    padding: 2px;

                    .options-dropdown-item {
                        padding: 8px 10px;
                        gap: 8px;

                        .options-dropdown-item-meta {
                            white-space: nowrap;
                        }
                    }
                }

                .range-popover {
                    max-width: calc(100vw - 24px);
                }
            }
        }
    }
}
</style>
